import instance from 'services/api';
import { gatewayInstance } from 'services/api';

export const generateProductInventory =
  (productId, businessUuid) => async (dispatch) => {
    try {
      const response = await gatewayInstance.post(
        `/api01/products/${productId}/generate-inventory`,
        { businessUuid }
      );
      return response;
    } catch (error) {
      console.error(error);
      return Promise.reject(error);
    }
  };

export const createInventoryToProduct = async (requestData, productId) => {
  try {
    const response = await instance.post(
      `products/${productId}/inventory`,
      requestData
    );
    return response;
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const deleteInventoryToProduct = (productId, uuid) => async () => {
  try {
    const response = await instance.delete(
      `products/${productId}/inventory/${uuid}`
    );
    return response;
  } catch (error) {
    console.error(error);
    return Promise.reject(error);
  }
};

export const getBusinessInventoryList =
  (businessUUid) => async (dispatch, getState) => {
    // Avoid duplicate concurrent fetches (the inventory tab mounts two
    // components that both request the list on mount).
    if (getState()?.inventory?.businessInventoryListLoading) {
      return;
    }
    dispatch({ type: 'SET_BUSINESS_INVENTORY_LIST_LOADING', payload: true });
    try {
      const response = await instance.get(
        `inventory/business/${businessUUid}`,
        // This is a passive read; don't surface a success toast for it.
        { skipSuccessToast: true }
      );
      dispatch({
        type: 'GET_BUSINESS_INVENTORY_LIST',
        payload: response?.data?.data,
      });
      return response;
    } catch (error) {
      return Promise.reject(error);
    } finally {
      dispatch({ type: 'SET_BUSINESS_INVENTORY_LIST_LOADING', payload: false });
    }
  };

export const updateInventoryBarcodeNumber = (requestData) => async () => {
  try {
    const response = await instance.put(
      `inventory/update-barcodes`,
      requestData
    );
    return response;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const updateInventorySku = (requestData) => async () => {
  try {
    const response = await instance.put(`/inventory/update-sku`, requestData);
    return response;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const updateInventoryUnit = (requestData) => async () => {
  try {
    const response = await instance.put(`/inventory/update-unit`, requestData);
    return response;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const addInventoryStock = (requestData) => async () => {
  try {
    const updatedRequestData = {
      ...requestData,
      quantity: Number(requestData.stock),
    };
    delete updatedRequestData.stock;
    const response = await instance.put(
      `/inventory/add-stock`,
      updatedRequestData
    );
    return response;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const removeInventoryStock = (requestData) => async () => {
  try {
    const updatedRequestData = {
      ...requestData,
      quantity: Number(requestData.stock),
    };
    delete updatedRequestData.stock;
    const response = await instance.put(
      `/inventory/remove-stock`,
      updatedRequestData
    );
    return response;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const adjustInventoryStock = (requestData) => async () => {
  try {
    const updatedRequestData = {
      ...requestData,
      quantity: Number(requestData.stock),
    };
    delete updatedRequestData.stock;
    const response = await instance.put(
      `/inventory/adjust-stock`,
      updatedRequestData
    );
    return response;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const updateInventoryItem =
  (requestData, productId, uuid) => async () => {
    try {
      const response = await instance.patch(
        `/products/${productId}/inventory/${uuid}`,
        requestData
      );
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };

export const updateProductUseInventory =
  (productId, requestData) => async () => {
    try {
      const response = await instance.patch(
        `/products/${productId}/use-inventory`,
        requestData
      );
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };

export const updateTrackInventory = (requestData) => async () => {
  try {
    const response = await instance.put(
      `/inventory/update-track-inventory`,
      requestData
    );
    return response;
  } catch (error) {
    return Promise.reject(error);
  }
};

// ===== NEW INVENTORY API ACTIONS =====

// Category Management Actions
export const getCategoriesByMenu = () => async (dispatch) => {
  try {
    const response = await instance.get(`/menu-inventory/categories/menu`);
    const apiData = response?.data?.data || {};
    dispatch({
      type: 'GET_CATEGORIES_BY_MENU',
      payload: {
        categories: apiData.categories || apiData || {},
        totalCount: apiData.totalCount || 0,
        changeCount: apiData.changeCount || 0,
      },
    });
    return response;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getCategoryById = (categoryId) => async () => {
  try {
    const response = await instance.get(
      `/menu-inventory/categories/${categoryId}`
    );
    return response;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const createCategory = (requestData) => async () => {
  try {
    const response = await instance.post(
      `/menu-inventory/categories`,
      requestData
    );
    return response;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const updateCategory = (categoryId, requestData) => async () => {
  try {
    const response = await instance.patch(
      `/menu-inventory/categories/${categoryId}`,
      requestData
    );
    return response;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const deleteCategory = (categoryId) => async () => {
  try {
    const response = await instance.delete(
      `/menu-inventory/categories/${categoryId}`
    );
    return response;
  } catch (error) {
    return Promise.reject(error);
  }
};

// Product Management Actions
export const getProductsByCategory = (categoryId) => async (dispatch) => {
  try {
    const response = await instance.get(
      `/menu-inventory/products/category/${categoryId}`
    );
    dispatch({
      type: 'GET_PRODUCTS_BY_CATEGORY',
      payload: response?.data?.data,
    });
    return response;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getProductsByMenu = () => async (dispatch) => {
  try {
    const response = await instance.get(`/menu-inventory/products/menu`);
    // Store both products object and total count
    const apiData = response?.data?.data || {};
    dispatch({
      type: 'GET_PRODUCTS_BY_MENU',
      payload: {
        products: apiData.products || {},
        totalCount: apiData.totalCount || 0,
        changeCount: apiData.changeCount || 0,
        totalInventory: apiData.totalInventory || 0,
        totalCost: apiData.totalCost || 0,
        totalSales: apiData.totalSales || 0,
      },
    });
    return response;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const getProductById = (productId) => async () => {
  try {
    const response = await instance.get(
      `/menu-inventory/products/${productId}`
    );
    return response;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const createProduct = (requestData) => async () => {
  try {
    const response = await instance.post(
      `/menu-inventory/products`,
      requestData
    );
    return response;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const updateProduct = (productId, requestData) => async () => {
  try {
    const response = await instance.patch(
      `/menu-inventory/products/${productId}`,
      requestData
    );
    return response;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const deleteProduct = (productId) => async () => {
  try {
    const response = await instance.delete(
      `/menu-inventory/products/${productId}`
    );
    return response;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const checkDuplicateBarcodes =
  (barcodes, productId = null) =>
  async () => {
    try {
      const response = await instance.post(
        `/menu-inventory/products/check-barcodes`,
        { barcodes, productId }
      );
      return response;
    } catch (error) {
      return Promise.reject(error);
    }
  };
